import React, { useEffect, useState, useContext } from "react";
import { Switch, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import { getApi, saveApi } from "../../helpers";
import CardContainer from "../../components/Card";
import TableContainer from "../../components/Table";
import { StoreContext } from "../../context";

export default function PaymentMethods() {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [savedPayload, setSavedPayload] = useState({});
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const [updating, setUpdating] = useState({});

  useEffect(() => getList(savedPayload), []);

  const getList = async (payload = {}) => {
    setLoading(true);
    const res = await getApi(payload, "getPaymentMethods", "ecomm/");
    if (res.data) {
      setList(res.data);
      setTotal(res.meta?.total || res.data.length);
    }
    setSavedPayload(payload);
    setLoading(false);
  };

  const handleStatusChange = async (paymentMethodId, isActive) => {
    const paymentMethod = list.find(item => item.payment_method_id === paymentMethodId);
    const action = isActive ? "activate" : "deactivate";
    
    Modal.confirm({
      title: `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to ${action} the payment method "${paymentMethod?.payment_method}"?`,
      okText: `Yes, ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      cancelText: "Cancel",
      okType: isActive ? "primary" : "danger",
      onOk: async () => {
        setUpdating({ ...updating, [paymentMethodId]: true });
        
        const payload = {
          payment_method_id: paymentMethodId,
          is_active: isActive ? 1 : 0,
        };
        
        const res = await saveApi(
          payload,
          isActive ? "Payment method activated successfully" : "Payment method deactivated successfully",
          "updatePaymentMethod",
          "ecomm/"
        );
        
        if (res.success) {
          // Update the local state instead of reloading
          setList(prevList => 
            prevList.map(item => 
              item.payment_method_id === paymentMethodId 
                ? { ...item, is_active: isActive ? 1 : 0 }
                : item
            )
          );
        }
        
        setUpdating({ ...updating, [paymentMethodId]: false });
      },
      onCancel: () => {
        // Reset the switch to its previous state if user cancels
        // The switch will automatically revert since we're not updating the state
      },
    });
  };

  // Empty row click handler since you don't want row click functionality
  const handleRowClick = () => {
    // Do nothing
  };

  const columns = [
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      render: (text) => (
        <div style={{ fontWeight: 500 }}>{text}</div>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Last Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 180,
      sorter: (a, b) => new Date(a.updated_at) - new Date(b.updated_at),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      width: 150,
      render: (isActive, record) => (
        <Switch
          checked={isActive === 1}
          loading={updating[record.payment_method_id]}
          onChange={(checked) => handleStatusChange(record.payment_method_id, checked)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
  ];

  return (
    <CardContainer hoverable={false}>
      <TableContainer
        breadcrumbTitle="Payment Methods Management"
        loading={loading}
        columns={columns}
        data={list}
        total={total}
        searchFor="Payment Method"
        functionsHandler={getList}
        rowClickHandler={handleRowClick}
      />
    </CardContainer>
  );
}